import { FC } from 'react';
import { Button, Col, Flex, Image, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT, REPORT_DATE_FORMAT } from 'constants/dates';
import { REPORT_INFO_TYPES } from 'constants/reports';
import logo from 'assets/img/logo_report.png';
import dayjs from 'dayjs';
import { useReportValuesStateContext } from 'providers/ReportValues';
import { useAuthStateContext } from 'providers/Auth';

import {
  AvatarContainer,
  InfoContainer,
  SignatureContainer,
  StyledPage,
  StyledPreviewContainer,
  TitleContainer
} from './styled';
import { getFileUrl } from 'utils/file';

const { Title, Text } = Typography;

interface IProps {
  step: number;
  content: string;
  signature: string;
  appointmentId?: string | null;
  type: keyof typeof REPORT_INFO_TYPES | string;
  onClickPreview: () => void;
}

const LivePreview: FC<IProps> = ({
  step,
  type,
  content,
  signature,
  appointmentId,
  onClickPreview
}) => {
  const values = useReportValuesStateContext();

  const { t } = useTranslation();
  const { user: meData } = useAuthStateContext();

  const lng = values.lang || 'en';

  return (
    <StyledPreviewContainer>
      <StyledPage>
        <div style={{ flex: 1 }}>
          <Row align="middle" justify="space-between">
            <Col>
              {appointmentId ? (
                <Image src={logo} preview={false} />
              ) : (
                <Text
                  style={{
                    color: '#0B1A3033',
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}
                >
                  {meData?.first_name} {meData?.last_name}
                </Text>
              )}
            </Col>
            <Col>
              <Text style={{ fontWeight: 500, color: '#0B1A30' }}>
                {t('reports.date', {
                  lng
                })}{' '}
                {values.report_date
                  ? dayjs(values.report_date.toString()).format(
                      REPORT_DATE_FORMAT
                    )
                  : '-'}
              </Text>
            </Col>
          </Row>

          <AvatarContainer>
            {type === REPORT_INFO_TYPES.doctor ? (
              <>
                <TitleContainer>
                  <Title level={5}>{t('reports.about_doctor', { lng })}</Title>
                </TitleContainer>
                <Image src={getFileUrl(meData?.avatar)} preview={false} />
                <Text>
                  {meData?.first_name} {meData?.last_name}
                </Text>
              </>
            ) : (
              <>
                <TitleContainer>
                  <Title level={5}>{t('reports.report', { lng })}</Title>
                </TitleContainer>
                <Text>
                  <Text style={{ fontWeight: 'bold' }}>
                    {t('reports.patient', { lng })}
                  </Text>
                  {` ${values.first_name || ''} ${values.last_name || ''} (${t(
                    'reports.dob',
                    { lng }
                  )}: ${
                    values.date_of_birth
                      ? dayjs(values.date_of_birth.toString()).format(
                          DATE_FORMAT
                        )
                      : '-'
                  })`}
                </Text>
                {!appointmentId && (
                  <Text>
                    <Text style={{ fontWeight: 'bold' }}>
                      {t('reports.patient_email', { lng })}
                    </Text>
                    {values.email}
                  </Text>
                )}
              </>
            )}
          </AvatarContainer>
          <div
            className="live-preview-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
          {type === REPORT_INFO_TYPES.patient && values.icd.length ? (
            <Flex gap={4} vertical>
              <Text
                style={{
                  color: '#0B1A3033',
                  fontSize: 12,
                  fontWeight: 'bold'
                }}
              >
                {t('reports.report_icd')}
              </Text>
              <Flex vertical>
                {values.icd.map(item => (
                  <Text key={item.id + item.title}>{item.title}</Text>
                ))}
              </Flex>
            </Flex>
          ) : null}
        </div>
        <InfoContainer>
          <SignatureContainer>
            <Image src={signature} preview={false} />
          </SignatureContainer>
          <Text
            style={{ textAlign: 'center', fontSize: 10, color: '#0B1A3033' }}
          >
            {meData?.first_name} {meData?.last_name}
          </Text>
          {appointmentId ? (
            <Text
              style={{
                textAlign: 'center',
                fontSize: 12,
                color: '#0B1A3033',
                paddingTop: 20
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: t('reports.medicus_info', {
                    lng
                  })
                }}
              />
            </Text>
          ) : (
            <Text
              style={{ textAlign: 'center', fontSize: 10, color: '#0B1A3033' }}
            >
              {t('reports.powered', { lng })}
            </Text>
          )}
        </InfoContainer>
      </StyledPage>
      <Row justify="space-between" align="middle" style={{ paddingTop: 20 }}>
        <Col>
          <Text style={{ fontWeight: 500 }}>
            {t('reports.report_page', { lng })}{' '}
            {step + 1 + (appointmentId ? 1 : 0)}/{appointmentId ? 3 : 2}
          </Text>
        </Col>
        {step === 1 && (
          <Col>
            <Button
              data-element="preview-report-button"
              type="link"
              onClick={onClickPreview}
            >
              <Text
                style={{
                  fontWeight: 500,
                  textDecoration: 'underline',
                  color: 'var(--primary-color)'
                }}
              >
                {t('reports.preview', { lng })}
              </Text>
            </Button>
          </Col>
        )}
      </Row>
    </StyledPreviewContainer>
  );
};

export default LivePreview;
